import React from 'react';

import classNames from 'classnames';

import { InlineAlert } from '../InlineAlert';

import type { DenseSizes } from './types';

const filledInputClassName = 'top-xxs text-sm';
const focusedInput = 'peer-focus:top-xxs! peer-focus:text-sm!';
const placeholderVisible = 'peer-placeholder-shown:top-xxs! peer-placeholder-shown:text-sm!';
const emptyInputClassName = `top-sm text-b2 ${focusedInput} ${placeholderVisible}`;

const borderClassName = 'hover:outline-hover focus-within:outline-brand!';
const disabledClassName = 'ctn-neutral-grey-weak text-disabled!';
const placeholderClassName = 'placeholder:text-disabled placeholder:opacity-100';

export const iconSuffixClassName = 'w-lg flex shrink-0 icon-secondary items-center';
export const textSuffixClassName =
  'flex items-center text-b2 font-medium text-secondary whitespace-nowrap';

export const makeInputContainerClassnames = ({
  disabled,
  error,
  small,
}: {
  disabled?: boolean;
  error?: string;
  small?: boolean;
}): string =>
  classNames(
    error ? 'outline-strong-danger' : borderClassName,
    disabled ? classNames(disabledClassName) : 'ctn-neutral-white',
    'relative outline flex rounded',
    small ? 'pr-xxs' : 'pr-sm',
  );

export const makeInputMainClassnames = ({
  disabled,
  hasPrefix,
  hasLabel,
  dense,
  hasControls,
}: {
  disabled?: boolean;
  hasPrefix?: boolean;
  hasLabel?: boolean;
  dense?: DenseSizes;
  hasControls?: boolean;
}): string =>
  classNames(
    placeholderClassName,
    { 'placeholder:text-tertiary': dense && !disabled },
    { [classNames(disabledClassName)]: disabled },
    { 'py-[3px]!': dense === 'md' },
    { 'py-3xs! text-b3!': dense === 'sm' },
    // We use margin top here to permit TextArea to scroll correctly when it needs to
    hasLabel ? 'mt-[20px] pt-0 pb-xxs' : 'py-sm',
    hasControls ? 'mb-[49px]' : '',
    hasPrefix ? 'pl-xxs' : 'pl-md',
    dense ? 'pl-xs! pr-xxs!' : 'pr-0',
    'w-full mr-xs peer border-0 outline-none text-b2 text-primary rounded',
  );

export const makeInputLabelClassnames = ({
  value,
  disabled,
  hasPrefix,
}: {
  value?: string | boolean;
  disabled?: boolean;
  hasPrefix?: boolean;
}): string =>
  classNames(
    value || hasPrefix ? filledInputClassName : emptyInputClassName,
    { 'text-disabled!': disabled },
    'absolute pl-md transition-all text-tertiary',
  );

interface InputHelperTextProps extends React.HTMLProps<HTMLSpanElement> {
  helperText?: string | React.ReactElement;
}
export const InputHelperText: React.FC<InputHelperTextProps> = ({ helperText, ...rest }) =>
  helperText ? (
    <span className="mt-xxs text-sm text-tertiary inline-block" {...rest}>
      {helperText}
    </span>
  ) : null;

interface InputErrorTextProps {
  error?: string;
}
export const InputErrorText: React.FC<InputErrorTextProps> = ({ error }) =>
  error ? (
    <div className="mt-xxs">
      <InlineAlert variant="error">{error}</InlineAlert>
    </div>
  ) : null;
